import { useState } from "react";
import type { Editor } from "prosekit/core";
import { useEditorDerivedValue } from "prosekit/react";
import { TextSelection } from "prosemirror-state";
import { pick } from "lodash";

import Button from "./ToolbarButton";
import type { EditorExtension } from "./extension";

import { useEditorContext } from "./EditorContext";
import {
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faPencilSquare,
  faCode,
  faListUl,
  faListOl,
  faQuoteRight,
  faLink,
  faFont,
  faFaceSmile,
  faHeading,
  faArrowRotateLeft,
  faExpand,
  faCompress,
} from "@fortawesome/pro-regular-svg-icons";
import { ToolbarWrap, Separator } from "./ToolbarUtils";
import { aiButtonState } from "./aiButtonState";
import LinkToolbar from "./LinkToolbar";
import TextColorToolbar from "./TextColorToolbar";
import EmojiToolbar from "./EmojiToolbar";
import HeadlineToolbar from "./HeadlineToolbar";
import AiToolbar from "./AiToolbar";
import CustomConfigurator from "../../../CustomConfigurator";
import AdaptiveIcon from "@/components/shared/AdaptiveIcon.tsx";
import getLinkRange from "@/components/shared/markdownEditor/getLinkRange";
import isLinkActive from "@/components/shared/markdownEditor/isLinkActive";
import { aiPencil } from "@/icons/customIcons.ts";

function getToolbarItems(editor: Editor<EditorExtension>) {
  return {
    bold: {
      isActive: editor.marks.bold.isActive(),
      canExec: editor.commands.toggleBold.canExec(),
      command: () => editor.commands.toggleBold(),
    },
    italic: {
      isActive: editor.marks.italic.isActive(),
      canExec: editor.commands.toggleItalic.canExec(),
      command: () => editor.commands.toggleItalic(),
    },
    underline: {
      isActive: editor.marks.underline.isActive(),
      canExec: editor.commands.toggleUnderline.canExec(),
      command: () => editor.commands.toggleUnderline(),
    },
    strike: {
      isActive: editor.marks.strike.isActive(),
      canExec: editor.commands.toggleStrike.canExec(),
      command: () => editor.commands.toggleStrike(),
    },
    code: {
      isActive: editor.marks.code.isActive(),
      canExec: editor.commands.toggleCode.canExec(),
      command: () => editor.commands.toggleCode(),
    },
    // TODO heading dropdown
    heading1: {
      isActive: editor.nodes.heading.isActive({ level: 1 }),
      canExec: editor.commands.toggleHeading.canExec({ level: 1 }),
      command: () => editor.commands.toggleHeading({ level: 1 }),
    },
    heading2: {
      isActive: editor.nodes.heading.isActive({ level: 2 }),
      canExec: editor.commands.toggleHeading.canExec({ level: 2 }),
      command: () => editor.commands.toggleHeading({ level: 2 }),
    },
    heading3: {
      isActive: editor.nodes.heading.isActive({ level: 3 }),
      canExec: editor.commands.toggleHeading.canExec({ level: 3 }),
      command: () => editor.commands.toggleHeading({ level: 3 }),
    },
    heading4: {
      isActive: editor.nodes.heading.isActive({ level: 4 }),
      canExec: editor.commands.toggleHeading.canExec({ level: 4 }),
      command: () => editor.commands.toggleHeading({ level: 4 }),
    },
    heading5: {
      isActive: editor.nodes.heading.isActive({ level: 5 }),
      canExec: editor.commands.toggleHeading.canExec({ level: 5 }),
      command: () => editor.commands.toggleHeading({ level: 5 }),
    },
    bullet: {
      isActive: editor.nodes.list.isActive({ kind: "bullet" }),
      canExec: editor.commands.toggleList.canExec({ kind: "bullet" }),
      command: () => editor.commands.toggleList({ kind: "bullet" }),
    },
    ordered: {
      isActive: editor.nodes.list.isActive({ kind: "ordered" }),
      canExec: editor.commands.toggleList.canExec({ kind: "ordered" }),
      command: () => editor.commands.toggleList({ kind: "ordered" }),
    },
    blockquote: {
      isActive: editor.nodes.blockquote.isActive(),
      canExec: editor.commands.toggleBlockquote.canExec(),
      command: () => editor.commands.setBlockquote(),
    },
    link: {
      isActive: isLinkActive(editor.state),
      canExec: editor.commands.addLink.canExec({ href: "" }),
      // Get the current link's href if a link is selected
      getCurrentHref: () => {
        try {
          const { selection, schema } = editor.state;
          const linkType = schema.marks.link;

          if (!selection.empty) {
            let foundHref: string | null = null;
            editor.state.doc.nodesBetween(
              selection.from,
              selection.to,
              (node) => {
                if (!node.isText) return;
                const link = node.marks.find((m) => m.type === linkType);
                if (link && !foundHref) {
                  foundHref = link.attrs.href;
                }
              },
            );
            return foundHref || "";
          }

          const range = getLinkRange(editor.state);
          if (range) {
            return range.href;
          }

          return "";
        } catch (error) {
          console.warn("Fehler beim Ermitteln der Link-URL:", error);
          return "";
        }
      },

      removeLink: () => editor.commands.removeLink(),
      addLink: (href: string) => editor.commands.addLink({ href }),
      editor: editor,
    },
    textColor: {
      isColorActive: (color: string | null) =>
        Boolean(color && editor.marks.textColor.isActive({ color })),
      isAnyActive: CustomConfigurator.get("markdownEditor.colors").some(
        ({ style: color }) => editor.marks.textColor.isActive({ color }),
      ),
      canExec: true, //editor.commands.toggleTextColor.canExec({ color: null }),
      command: (color: string | null) => {
        if (!color) {
          editor.commands.removeTextColor();
        } else {
          editor.commands.toggleTextColor({ color });
        }
      },
    },
    highlight: {
      isActive: editor.marks.highlight.isActive(),
      canExec: editor.commands.toggleHighlight.canExec(),
      command: () => editor.commands.toggleHighlight(),
    },
    emoji: {
      isActive: false,
      canExec: true,
      command: (emoji: string) => {
        editor.updateState(
          editor.state.apply(
            editor.state.tr.insertText(emoji).setMeta("addToHistory", true),
          ),
        );
      },
    },
    undo: {
      isActive: false,
      canExec: editor.commands.undo.canExec(),
      command: () => editor.commands.undo(),
    },
    redo: {
      isActive: false,
      canExec: editor.commands.redo.canExec(),
      command: () => editor.commands.redo(),
    },
  };
}

const emojis = [
  "😀",
  "😂",
  "🙂",
  "😍",
  "😎",
  "🤔",
  "👍",
  "👎",
  "👏",
  "🙏",
  "🎉",
  "❤️",
  "🔥",
  "⭐",
  "✅",
  "❌",
];

export default function Toolbar() {
  const { readOnly, isFullscreen, toggleFullscreen } = useEditorContext();
  const items = useEditorDerivedValue(getToolbarItems);

  const [toolbar, setToolbar] = useState<string | null>(null);
  const [linkHref, setLinkHref] = useState<string>("");

  const handleLinkToolbar = () => {
    const editor = items.link.editor;
    if (!editor) return;

    const { state, view } = editor;

    try {
      const currentHref = items.link.getCurrentHref();
      setLinkHref(currentHref);

      if (items.link.isActive && state.selection.empty) {
        const range = getLinkRange(state);
        if (range) {
          view.dispatch(
            state.tr.setSelection(
              TextSelection.create(state.doc, range.from, range.to),
            ),
          );
        } else {
          setLinkHref("");
        }
      }
    } catch (error) {
      console.warn("Fehler beim Öffnen der Link-Toolbar:", error);
      setLinkHref("");
    }

    setToolbar("link");
  };

  if (toolbar == "link") {
    return (
      <LinkToolbar
        linkHref={linkHref}
        setLinkHref={setLinkHref}
        onBack={() => setToolbar(null)}
        onRemoveLink={() => {
          items.link.removeLink();
          setToolbar(null);
        }}
        onAddLink={(href) => {
          items.link.addLink(href);
          setToolbar(null);
        }}
      />
    );
  }

  if (toolbar == "textColor") {
    return (
      <TextColorToolbar
        isActive={items.textColor.isColorActive}
        canExec={items.textColor.canExec}
        onSetColor={(color) => items.textColor.command(color)}
        onBack={() => setToolbar(null)}
      />
    );
  }

  if (toolbar == "emoji") {
    return (
      <EmojiToolbar
        emojis={emojis}
        onInsertEmoji={(emoji) => {
          items.emoji.command(emoji);
          setToolbar(null);
        }}
        onBack={() => setToolbar(null)}
      />
    );
  }

  if (toolbar == "headline") {
    const headings = pick(items, [
      "heading1",
      "heading2",
      "heading3",
      "heading4",
      "heading5",
    ]);

    return (
      <HeadlineToolbar headings={headings} onBack={() => setToolbar(null)} />
    );
  }

  if (toolbar == "ai") {
    return <AiToolbar onBack={() => setToolbar(null)} />;
  }

  return (
    <ToolbarWrap>
      <Button
        pressed={items.bold.isActive}
        disabled={readOnly || !items.bold.canExec}
        onClick={items.bold.command}
        tooltip={I18n.t("js.composer.editor.buttons.bold")}
      >
        <AdaptiveIcon icon={faBold} />
      </Button>
      <Button
        pressed={items.italic.isActive}
        disabled={readOnly || !items.italic.canExec}
        onClick={items.italic.command}
        tooltip={I18n.t("js.composer.editor.buttons.italic")}
      >
        <AdaptiveIcon icon={faItalic} />
      </Button>
      <Button
        pressed={items.underline.isActive}
        disabled={readOnly || !items.underline.canExec}
        onClick={items.underline.command}
        tooltip={I18n.t("js.composer.editor.buttons.underline")}
      >
        <AdaptiveIcon icon={faUnderline} />
      </Button>
      <Button
        pressed={items.strike.isActive}
        disabled={readOnly || !items.strike.canExec}
        onClick={items.strike.command}
        tooltip={I18n.t("js.composer.editor.buttons.strikethrough")}
      >
        <AdaptiveIcon icon={faStrikethrough} />
      </Button>
      <Separator />
      <Button
        pressed={
          items.heading1.isActive ||
          items.heading2.isActive ||
          items.heading3.isActive ||
          items.heading4.isActive ||
          items.heading5.isActive
        }
        disabled={
          readOnly ||
          (!items.heading1.canExec &&
            !items.heading2.canExec &&
            !items.heading3.canExec &&
            !items.heading4.canExec &&
            !items.heading5.canExec)
        }
        onClick={() => {
          setToolbar("headline");
        }}
        tooltip={I18n.t("js.composer.editor.buttons.headlines")}
      >
        <AdaptiveIcon icon={faHeading} />
      </Button>
      <Button
        pressed={items.bullet.isActive}
        disabled={readOnly || !items.bullet.canExec}
        onClick={items.bullet.command}
        tooltip={I18n.t("js.composer.editor.buttons.unordered_list")}
      >
        <AdaptiveIcon icon={faListUl} />
      </Button>
      <Button
        pressed={items.ordered.isActive}
        disabled={readOnly || !items.ordered.canExec}
        onClick={items.ordered.command}
        tooltip={I18n.t("js.composer.editor.buttons.ordered_list")}
      >
        <AdaptiveIcon icon={faListOl} />
      </Button>
      <Button
        pressed={items.blockquote.isActive}
        disabled={readOnly || !items.blockquote.canExec}
        onClick={items.blockquote.command}
        tooltip={I18n.t("js.composer.editor.buttons.blockquote")}
      >
        <AdaptiveIcon icon={faQuoteRight} />
      </Button>
      <Separator />
      <Button
        pressed={items.textColor.isAnyActive}
        disabled={readOnly || !items.textColor.canExec}
        onClick={() => {
          setToolbar("textColor");
        }}
        tooltip={I18n.t("js.composer.editor.buttons.colors")}
      >
        <AdaptiveIcon icon={faFont} />
      </Button>
      <Button
        pressed={items.highlight.isActive}
        disabled={readOnly || !items.highlight.canExec}
        onClick={items.highlight.command}
        tooltip={I18n.t("js.composer.editor.buttons.mark")}
      >
        <AdaptiveIcon icon={faPencilSquare} />
      </Button>
      <Button
        pressed={items.link.isActive}
        disabled={readOnly || !items.link.canExec}
        onClick={handleLinkToolbar}
        tooltip={I18n.t("js.composer.editor.buttons.link")}
      >
        <AdaptiveIcon icon={faLink} />
      </Button>
      <Button
        pressed={items.code.isActive}
        disabled={readOnly || !items.code.canExec}
        onClick={items.code.command}
        tooltip={I18n.t("js.composer.editor.buttons.code")}
      >
        <AdaptiveIcon icon={faCode} />
      </Button>
      <Button
        pressed={false}
        disabled={readOnly || !items.emoji.canExec}
        onClick={() => {
          setToolbar("emoji");
        }}
        tooltip={I18n.t("js.composer.editor.buttons.emoji.tooltip")}
      >
        <AdaptiveIcon icon={faFaceSmile} />
      </Button>
      {(() => {
        const ai = aiButtonState(readOnly);
        return ai.visible ? (
          <Button
            pressed={false}
            disabled={ai.disabled}
            onClick={() => {
              setToolbar("ai");
            }}
            tooltip={ai.tooltip}
          >
            <AdaptiveIcon
              icon={aiPencil}
              color={ai.disabled ? "#9ca3af" : "#ef2c61"}
            />
          </Button>
        ) : null;
      })()}
      <div className="grow" />
      <div className="flex items-center flex-nowrap">
        <Button
          pressed={items.undo.isActive}
          disabled={readOnly || !items.undo.canExec}
          onClick={items.undo.command}
          tooltip={I18n.t("js.composer.editor.undo.hint")}
        >
          <AdaptiveIcon icon={faArrowRotateLeft} />
        </Button>
        <Button
          pressed={items.redo.isActive}
          disabled={readOnly || !items.redo.canExec}
          onClick={items.redo.command}
          tooltip={I18n.t("js.composer.editor.redo.hint")}
        >
          <AdaptiveIcon icon={faArrowRotateLeft} flip="horizontal" />
        </Button>
      </div>
      <Button
        pressed={isFullscreen}
        onClick={toggleFullscreen}
        tooltip={
          isFullscreen
            ? I18n.t("js.composer.editor.exitfullscreen.hint")
            : I18n.t("js.composer.editor.fullscreen.hint")
        }
      >
        <AdaptiveIcon icon={isFullscreen ? faCompress : faExpand} />
      </Button>
    </ToolbarWrap>
  );
}
